import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { formPaginatedListSchema } from '@valuecase/common'
import { useSpacesApi } from '../../../spaces-api'

/**
 * Query key factory for forms list queries
 */
export const buildFormsQueryKey = ({
  spaceIds,
  page,
  itemsPerPage,
  includeDeleted,
  includeNotCompleted,
  completedAfter,
  includeSubmissions = false,
}: {
  spaceIds: string[]
  page: number
  itemsPerPage: number
  includeDeleted: boolean
  includeNotCompleted: boolean
  completedAfter?: string
  includeSubmissions: boolean
}) => {
  return [
    'forms',
    'list',
    spaceIds,
    page,
    itemsPerPage,
    includeDeleted,
    includeNotCompleted,
    completedAfter,
    includeSubmissions,
  ]
}

/**
 * Hook to fetch forms for a space
 */
export function useReadForms({
  spaceIds,
  page = 1,
  itemsPerPage = 10,
  includeDeleted = false,
  includeNotCompleted = false,
  completedAfter,
  includeSubmissions = false,
  staleTime = 1000 * 60,
}: {
  spaceIds: string[]
  page: number
  itemsPerPage: number
  includeDeleted: boolean
  includeNotCompleted: boolean
  completedAfter?: string
  includeSubmissions: boolean
  staleTime?: number
}) {
  const { spacesApi } = useSpacesApi()

  const queryKey = useMemo(() => {
    if (!spaceIds) {
      return ['forms', 'list', null]
    }

    return buildFormsQueryKey({
      spaceIds,
      page,
      itemsPerPage,
      includeDeleted,
      includeNotCompleted,
      completedAfter,
      includeSubmissions,
    })
  }, [
    spaceIds,
    page,
    itemsPerPage,
    includeDeleted,
    includeNotCompleted,
    completedAfter,
    includeSubmissions,
  ])

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!spaceIds || !spacesApi) {
        throw new Error('Space IDs and spacesApi are required')
      }

      try {
        const response = await spacesApi.get('/forms', {
          params: {
            spaceIds: [spaceIds.join(',')],
            page,
            itemsPerPage,
            includeDeleted,
            includeNotCompleted,
            completedAfter,
            includeSubmissions,
          },
        })
        return formPaginatedListSchema.parse(response.data)
      } catch (error) {
        if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
          throw new Error('Forms not found')
        }
        throw error
      }
    },
    enabled: !!spaceIds && !!spacesApi,
    staleTime,
  })

  return {
    ...query,
    forms: query.data?.items ?? [],
  }
}
